import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Button,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Card,
  message,
  Switch,
  TreeSelect,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  DownOutlined,
  UpOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import debounce from "lodash.debounce";
import { getPurchaseRecords, getAssetCategories } from "../api/asset";
import { getUserList } from "../api/user";
import { getDictItems } from "../DictCache";
import dayjs from "dayjs";
import RemoteSelect from "../components/common/RemoteSelect"; // 见文末"优化点 6"
import PurchaseRecordDrawer from "../components/assets/PurchaseRecordDrawer";
import PurchaseInboundDrawer from "../components/assets/PurchaseInboundDrawer";
const { Title, Text } = Typography;

const PAGE_SIZE = 20;
const DEFAULT_FILTERS = {
  purchaseBatch: "",
  description: "",
  assetCategory: undefined,
  cross_border_transit_provider: [],
  status: ["ordered", "delivering", "pending_pickup", "in_transit"],
  showUnSettledOnly: true,
  purchaser: undefined,
};
const DEBOUNCED_FIELDS = [
  "purchaseBatch",
  "description",
  "status",
  "cross_border_transit_provider",
]; // 这些字段防抖后查询，其余立即查
const MIN_LEN = { purchaseBatch: 3, description: 2 }; // 即时查询时文本短于该长度不算条件，按需调整

const toTree = (items = []) =>
  items.map((item) => ({
    title: item.name,
    value: item._id,
    key: item._id,
    children: item.children?.length ? toTree(item.children) : undefined,
  }));
const normalize = (all = {}) => ({
  purchaseBatch: all.purchaseBatch?.trim() || undefined,
  description: all.description?.trim() || undefined,
  assetCategory: all.assetCategory || undefined,
  purchaser: all.purchaser || undefined,
  cross_border_transit_provider: all.cross_border_transit_provider?.length
    ? all.cross_border_transit_provider
    : undefined,
  status: all.status?.length ? all.status : undefined,
  showUnSettledOnly: !!all.showUnSettledOnly,
});
const fetchPurchasers = async (name) => {
  const { data } = await getUserList({ name });
  return (data || []).map((u) => ({
    label: u?.name || u.username,
    value: u._id,
  }));
};

// 以下三项原先定义在组件内部，每次渲染都重建（StatusTag 还会导致组件被反复卸载重挂），统一提到模块顶层
const SOURCE_CONFIG = {
  jd: { label: "京东", color: "#e1251b", bg: "#fff2f0" },
  taobao: { label: "淘宝", color: "#ff6900", bg: "#fff7e6" },
  alibaba: { label: "阿里巴巴", color: "#ff6900", bg: "#fff7e6" },
  tm: { label: "天猫", color: "#c1001f", bg: "#fff2f0" },
  pdd: { label: "拼多多", color: "#e02e24", bg: "#fff2f0" },
  offline: { label: "线下", color: "#1677ff", bg: "#e6f4ff" },
  other: { label: "其他", color: "#8c8c8c", bg: "#fafafa" },
};
const STATUS_CONFIG = {
  待付款: { label: "待付款", color: "#faad14", bg: "#fffbe6" },
  ordered: { label: "已下单", color: "#1677ff", bg: "#e6f4ff" },
  delivering: { label: "运输中", color: "#722ed1", bg: "#f9f0ff" },
  pending_pickup: { label: "待提货", color: "#ff6900", bg: "#fff7e6" },
  warehoused: { label: "已入库", color: "#8c8c8c", bg: "#fafafa" },
  picked_up: { label: "已提货", color: "#8c8c8c", bg: "#fafafa" },
  in_transit: { label: "转运中", color: "#ff8018", bg: "#eeffe6" },
  canceled: { label: "已取消", color: "#8c8c8c", bg: "#fafafa" },
};

const StatusTag = ({ config }) =>
  config ? (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: 20,
        fontSize: 12,
        color: config.color,
        background: config.bg,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {config.label}
    </span>
  ) : null;

const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, cfg]) => ({
  value,
  label: <StatusTag config={cfg} />,
}));
const money = (value) => Number(value || 0).toLocaleString();

const FilterItem = ({ label, name, valuePropName, keepLabel, children }) => (
  <div className={`purchase-filter-row${keepLabel ? " keep-label" : ""}`}>
    <span className="purchase-filter-label">{label}</span>
    <Form.Item name={name} valuePropName={valuePropName} noStyle>
      {children}
    </Form.Item>
  </div>
);

// memo：props 引用稳定，列表加载不会重渲染筛选表单，输入时不会失焦
const FilterForm = React.memo(function FilterForm({
  form,
  categories,
  providers,
  onValuesChange,
  commit,
}) {
  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={DEFAULT_FILTERS}
      onValuesChange={onValuesChange}
      style={{ display: "grid", gap: 8, paddingTop: 12 }}
    >
      <FilterItem label="采购批次" name="purchaseBatch">
        <Input
          allowClear
          enterKeyHint="search"
          placeholder="至少输入 3 个字符"
          onBlur={commit}
          onPressEnter={commit}
        />
      </FilterItem>
      <FilterItem label="采购内容" name="description">
        <Input
          allowClear
          enterKeyHint="search"
          placeholder="至少输入 2 个字符"
          onBlur={commit}
          onPressEnter={commit}
        />
      </FilterItem>
      <FilterItem label="资产类目" name="assetCategory">
        <TreeSelect
          showSearch
          allowClear
          treeDefaultExpandAll
          treeNodeFilterProp="title"
          treeData={categories}
          placeholder="选择或输入类目"
        />
      </FilterItem>
      <FilterItem label="中转供应商" name="cross_border_transit_provider">
        <Select
          mode="multiple"
          maxTagCount={2}
          allowClear
          placeholder="选择中转供应商"
          options={providers}
        />
      </FilterItem>
      <FilterItem label="采购状态" name="status">
        <Select
          mode="multiple"
          maxTagCount={2}
          allowClear
          placeholder="全部状态"
          options={STATUS_OPTIONS}
        />
      </FilterItem>
      <FilterItem
        label="中转费结算"
        name="showUnSettledOnly"
        valuePropName="checked"
        keepLabel
      >
        <Switch checkedChildren="仅未结算" unCheckedChildren="全部数据" />
      </FilterItem>
      <FilterItem label="采购人" name="purchaser">
        <RemoteSelect
          fetcher={fetchPurchasers}
          notFoundContent="未找到采购人"
          placeholder="输入姓名搜索"
        />
      </FilterItem>
    </Form>
  );
});

// ─── 常量 ─────────────────────────────────────────────────────────────────────
// 可以入库的状态
const INBOUND_STATUSES = ["pending_pickup", "in_transit"];

// ─── 主页面 ───────────────────────────────────────────────────────────────────
export default function PurchaseRecordPage() {
  // const [filters, setFilters] = useState({
  //   purchaseBatch: "",
  //   description: "",
  //   assetCategory: "",
  //   cross_border_transit_provider: [],
  //   status: ["ordered", "delivering", "pending_pickup", "in_transit"],
  //   showUnSettledOnly: true,
  //   purchaser: "",
  // });
  const [dataSource, setDataSource] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [providerOptions, setProviderOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  // const [purchaserOptions, setPurchaserOptions] = useState([]);
  // const [purchaserSearching, setPurchaserSearching] = useState(false);
  // const purchaserSearchRef = React.useRef();
  // const purchaserRequestRef = React.useRef(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [inboundOpen, setInboundOpen] = useState(false);
  const [inboundRecord, setInboundRecord] = useState(null);
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);
  const requestId = useRef(0);
  const valuesRef = useRef(normalize(DEFAULT_FILTERS));
  const pageRef = useRef(1);
  const failedRef = useRef(false); // 上次请求失败时，相同条件允许重试

  const fetchRows = useCallback(
    async (nextPage = 1, values = valuesRef.current) => {
      const id = ++requestId.current; // 序号保护：丢弃过期响应
      valuesRef.current = values;
      setLoading(true);
      try {
        const { data } = await getPurchaseRecords({
          ...values,
          page: nextPage,
          pageSize: PAGE_SIZE,
        });
        if (id !== requestId.current) return;
        const count = data.total || 0;
        // 当前页被清空（如本页最后一条入库后不再满足筛选）：回退到最后一页
        if (!data.list?.length && nextPage > 1 && count > 0)
          return fetchRows(Math.ceil(count / PAGE_SIZE), values);
        setDataSource(data.list || []);
        setTotal(count);
        setPage(nextPage);
        pageRef.current = nextPage;
        failedRef.current = false;
      } catch (error) {
        if (id === requestId.current) {
          failedRef.current = true;
          message.error("采购记录加载失败");
        }
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    [],
  );
  useEffect(() => {
    fetchRows(1);
  }, [fetchRows]); // 替换原来依赖 fetchRecords 的那个 effect

  const commit = useCallback(() => {
    const values = normalize(form.getFieldsValue());
    if (
      !failedRef.current &&
      JSON.stringify(values) === JSON.stringify(valuesRef.current)
    )
      return;
    fetchRows(1, values);
  }, [form, fetchRows]);

  const instantSearch = useMemo(
    () =>
      debounce(() => {
        const values = normalize(form.getFieldsValue());
        for (const [key, min] of Object.entries(MIN_LEN)) {
          if (values[key] && values[key].length < min)
            values[key] = valuesRef.current[key]; // 过短的文本沿用上次的值
        }
        if (
          !failedRef.current &&
          JSON.stringify(values) === JSON.stringify(valuesRef.current)
        )
          return;
        fetchRows(1, values);
      }, 500),
    [form, fetchRows],
  );
  useEffect(() => () => instantSearch.cancel(), [instantSearch]);

  const commitNow = useCallback(() => {
    instantSearch.cancel();
    commit();
  }, [instantSearch, commit]);
  const onValuesChange = useCallback(
    (changed) => {
      if (Object.keys(changed).every((k) => DEBOUNCED_FIELDS.includes(k)))
        instantSearch();
      else commitNow();
    },
    [instantSearch, commitNow],
  );

  const resetFilters = () => {
    instantSearch.cancel();
    form.resetFields();
    fetchRows(1, normalize(DEFAULT_FILTERS));
  };
  const refresh = useCallback(() => fetchRows(pageRef.current), [fetchRows]); // 保存/入库后留在当前页刷新

  useEffect(() => {
    getDictItems("cross_border_transit_provider")
      .then(setProviderOptions)
      .catch(() => setProviderOptions([]));
  }, []);
  useEffect(() => {
    getAssetCategories()
      .then(({ data }) => setCategoryOptions(toTree(data || [])))
      .catch(() => setCategoryOptions([]));
  }, []);
  // useEffect(() => () => clearTimeout(purchaserSearchRef.current), []);

  // const searchPurchasers = (keyword) => {
  //   clearTimeout(purchaserSearchRef.current);
  //   const query = keyword?.trim();
  //   const requestId = ++purchaserRequestRef.current;
  //   if (!query) {
  //     setPurchaserOptions([]);
  //     setPurchaserSearching(false);
  //     return;
  //   }
  //   setPurchaserSearching(true);
  //   purchaserSearchRef.current = setTimeout(async () => {
  //     try {
  //       const { data } = await getUserList({ name: query });
  //       if (requestId === purchaserRequestRef.current) {
  //         setPurchaserOptions(
  //           (data || []).map((user) => ({
  //             label: user?.name || user.username,
  //             value: user._id,
  //           })),
  //         );
  //       }
  //     } catch {
  //       if (requestId === purchaserRequestRef.current) setPurchaserOptions([]);
  //     } finally {
  //       if (requestId === purchaserRequestRef.current)
  //         setPurchaserSearching(false);
  //     }
  //   }, 300);
  // };

  // const handleSearch = () => setPagination((p) => ({ ...p, current: 1 }));
  // const handleReset = () => {
  //   setFilters({
  //     purchaseBatch: "",
  //     description: "",
  //     assetCategory: "",
  //     cross_border_transit_provider: [],
  //     status: ["ordered", "delivering", "pending_pickup", "in_transit"],
  //     showUnSettledOnly: true,
  //     purchaser: "",
  //   });
  //   setPurchaserOptions([]);
  //   setPagination((p) => ({ ...p, current: 1 }));
  // };
  const openAdd = () => {
    setEditRecord(null);
    setFormOpen(true);
  };
  const openEdit = (record) => {
    setEditRecord(record);
    setFormOpen(true);
  };
  const openInbound = (record) => {
    setInboundRecord(record);
    setInboundOpen(true);
  };
  const openInboundFromDrawer = (record) => {
    setFormOpen(false);
    openInbound(record);
  };
  const chipBase = {
    flexShrink: 0,
    padding: "2px 7px",
    borderRadius: 5,
    fontSize: 11,
    lineHeight: "18px",
    whiteSpace: "nowrap",
  };
  const Chip = ({ label, value, color, bg, children }) => (
    <span style={{ ...chipBase, color, background: bg }}>
      {label && <span style={{ opacity: 0.75, marginRight: 3 }}>{label}</span>}
      {children ?? `¥${money(value)}`}
    </span>
  );
  return (
    <div
      style={{
        minHeight: "100dvh",
        padding: "16px 12px calc(24px + env(safe-area-inset-bottom))",
        background: "#f5f7fa",
        maxWidth: 760,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0, color: "#1f2329" }}>
            采购记录
          </Title>
          <Text type="secondary">共 {total} 条采购记录</Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openAdd}
          style={{ height: 42, borderRadius: 10 }}
        >
          新增采购记录
        </Button>
      </div>
      <Card
        styles={{ body: { padding: 0 } }}
        style={{ marginBottom: 12, borderRadius: 12, overflow: "hidden" }}
      >
        <button
          type="button"
          onClick={() => setFiltersOpen((open) => !open)}
          style={{
            width: "100%",
            height: 50,
            border: 0,
            background: "#fff",
            padding: "0 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "#313740",
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          <span>筛选采购记录</span>
          <Space>
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                resetFilters();
              }}
            >
              重置
            </Button>
            {filtersOpen ? <UpOutlined /> : <DownOutlined />}
          </Space>
        </button>
        {/* 用 display 控制显隐而不是条件渲染：收起再展开时表单不卸载，输入内容保留 */}
        <div
          style={{
            display: filtersOpen ? "block" : "none",
            padding: "0 14px 12px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <FilterForm
            form={form}
            categories={categoryOptions}
            providers={providerOptions}
            onValuesChange={onValuesChange}
            commit={commitNow}
          />
          <div style={{ marginTop: 10, fontSize: 11, color: "#94a3b8" }}>
            默认展示未提货/未入库记录，点右上角“重置”恢复默认筛选
          </div>
        </div>
      </Card>
      <style>{`
  .purchase-filter-row { display:flex; align-items:center; gap:10px; min-width:0; }
  .purchase-filter-label { flex:0 0 72px; color:#64748b; font-size:13px; white-space:nowrap; overflow:hidden; transition: flex-basis .15s, opacity .15s, margin-right .15s; }
  .purchase-filter-row > .ant-input-affix-wrapper, .purchase-filter-row > .ant-input, .purchase-filter-row > .ant-select, .purchase-filter-row > .ant-tree-select { flex:1; min-width:0; }
  .purchase-filter-row:not(.keep-label):focus-within .purchase-filter-label { flex-basis:0; opacity:0; margin-right:-10px; }
`}</style>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          opacity: loading ? 0.55 : 1,
          transition: "opacity .2s",
        }}
      >
        {dataSource.map((record) => {
          const status = STATUS_CONFIG[record.status] || {
            label: record.status || "未知状态",
            color: "#64748b",
            bg: "#f1f5f9",
          };
          const source = SOURCE_CONFIG[record.asset_procurement_source] || {
            label: record.asset_procurement_source || "—",
            color: "#64748b",
            bg: "#f1f5f9",
          };
          // const money = (value) => Number(value || 0).toLocaleString();
          return (
            <Card
              key={record._id}
              hoverable
              onClick={() => openEdit(record)}
              style={{ borderRadius: 12, cursor: "pointer" }}
              styles={{ body: { padding: "14px 14px 12px" } }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  minWidth: 0,
                  marginBottom: 9,
                }}
              >
                <Text
                  ellipsis
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontFamily: "ui-monospace,monospace",
                    color: "#475569",
                    fontSize: 13,
                  }}
                >
                  {record.purchaseBatch || "—"}
                </Text>
                <StatusTag config={status} />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 9,
                  minWidth: 0,
                }}
              >
                <Text
                  ellipsis
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 15,
                    color: "#1f2329",
                    fontWeight: 600,
                    textAlign: "left",
                  }}
                >
                  {record.description || "—"}
                </Text>
                <Text
                  style={{
                    flexShrink: 0,
                    color: "#64748b",
                    fontSize: 12,
                    textAlign: "right",
                  }}
                >
                  {record.assetCategory?.name || "—"}
                </Text>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  minWidth: 0,
                  color: "#64748b",
                  fontSize: 12,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      color: source.color,
                      background: source.bg,
                      padding: "2px 7px",
                      borderRadius: 5,
                    }}
                  >
                    {source.label}
                  </span>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 4,
                      padding: "2px 8px",
                      borderRadius: 6,
                      background: "rgba(82,196,26,0.12)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#389e0d",
                      }}
                    >
                      ¥{money(record.actualPayment)}
                      {Number(record.shippingFee) > 0 && (
                        <Chip
                          label="+"
                          value={record.shippingFee}
                          color="#0958d9"
                          // bg="rgba(22,119,255,0.12)"
                        />
                      )}
                    </span>
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    minWidth: 0,
                  }}
                >
                  <span style={{ flexShrink: 0 }}>
                    {record.orderTime
                      ? dayjs(record.orderTime).format("YYYY-MM-DD")
                      : "—"}
                  </span>
                  <span
                    style={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {record.purchaser?.name || "—"}
                  </span>
                </div>
              </div>
              <div
                style={{
                  borderTop: "1px solid #f1f5f9",
                  paddingTop: 9,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexShrink: 0,
                  }}
                >
                  <Chip
                    label="单价"
                    value={record.unitPrice}
                    color="#475569"
                    bg="rgba(100,116,139,0.12)"
                  />
                  <Chip label="X" color="#722ed1" bg="rgba(114,46,209,0.12)">
                    {money(record.quantity)}
                  </Chip>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 6,
                    flexWrap: "wrap",
                    minWidth: 0,
                  }}
                >
                  {Number(record.transitShippingFee) > 0 && (
                    <Chip
                      label="运费"
                      value={record.transitShippingFee}
                      color="#d46b08"
                      bg="rgba(250,140,22,0.14)"
                    />
                  )}
                </div>
              </div>
            </Card>
          );
        })}
        {!loading && dataSource.length === 0 && (
          <Card style={{ borderRadius: 12 }}>
            <div style={{ textAlign: "center", color: "#94a3b8", padding: 24 }}>
              暂无采购记录
            </div>
          </Card>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "center", marginTop: 14 }}>
        <Space>
          {total > PAGE_SIZE && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 14,
              }}
            >
              <Space>
                <Button
                  disabled={page <= 1}
                  onClick={() => fetchRows(page - 1)}
                >
                  上一页
                </Button>
                <Text type="secondary">
                  {page} / {Math.ceil(total / PAGE_SIZE)}
                </Text>
                <Button
                  disabled={page >= Math.ceil(total / PAGE_SIZE)}
                  onClick={() => fetchRows(page + 1)}
                >
                  下一页
                </Button>
              </Space>
            </div>
          )}
        </Space>
      </div>

      {/* 新增/编辑弹窗 */}
      <PurchaseRecordDrawer
        open={formOpen}
        editRecord={editRecord}
        onClose={() => setFormOpen(false)}
        onSuccess={refresh}
        onInbound={openInboundFromDrawer}
      />
      <PurchaseInboundDrawer
        open={inboundOpen}
        record={inboundRecord}
        onClose={() => setInboundOpen(false)}
        onSuccess={refresh}
      />
    </div>
  );
}
